import { VERIFICATION, STATS_UPDATED_AT } from "@/lib/siteStats";
import { formatCount, showCount } from "@/lib/statsFormat";

// Stats for the Verification API page. Values come from the single
// public/stats.json snapshot — see lib/siteStats.ts.
//
// Source: verification_api_history in the `customer` DB. Two filters shape
// these numbers, both applied server-side in StatsController::verificationStats:
// sandbox traffic (is_uat = 1) is excluded, and the success rate is measured
// only over requests that actually settled — status 1 (success) or 2 (failure),
// never the NULL rows that were never answered by the provider.

export const VERIFICATION_UPDATED_AT = STATS_UPDATED_AT;

// ---------------------------------------------------------------------------
// TEMPORARY — preview figures, delete once the backend is deployed.
//
// StatsController's `verification` section only exists locally so far, so
// stats.json has no verification key and the band hides itself. These stand-in
// numbers let the section be reviewed before the backend ships.
//
// They are invented. The `NODE_ENV` guard is what keeps them honest: in a
// production build this branch is never taken, so the live site either shows
// real figures or shows no band at all. Fake numbers cannot reach visitors even
// if this block is forgotten — which, being invented numbers on a page whose
// whole point is being trustworthy, is the only acceptable way to preview them.
const SAMPLE = { verifications: 128450, success_rate: 99.4, apis_live: 18, clients: 42 };

const SOURCE =
  VERIFICATION.verifications > 0 || process.env.NODE_ENV === "production"
    ? VERIFICATION
    : SAMPLE;
// ---------------------------------------------------------------------------

// The success rate arrives as a percentage already (99.4, not 0.994). One
// decimal, and never "100.0%" — a public reliability claim of exactly 100 reads
// as unmeasured, so it's floored to 99.9% while staying honest about the
// underlying figure, which the hover tooltip still shows.
export function formatRate(pct: number): string {
  if (pct <= 0) return "—";
  return `${Math.min(pct, 99.9).toFixed(1)}%`;
}

// The band renders unconditionally, by request: a missing figure shows as a
// dash rather than hiding the whole section. That makes a broken data feed
// visible instead of silent — the tradeoff being that a live page can show
// dashes if the `verification` section is absent from stats.json.
export const VERIFICATION_STATS = [
  {
    value: showCount(SOURCE.verifications),
    exact: SOURCE.verifications,
    target: SOURCE.verifications,
    format: formatCount,
    label: "Verifications Processed",
  },
  {
    value: formatRate(SOURCE.success_rate),
    // Percentages aren't counts — the tooltip would only ever repeat the
    // displayed figure, so pass 0 to suppress it (see StatNumber). `target`
    // carries the real percentage separately, purely for the count-up.
    exact: 0,
    target: SOURCE.success_rate,
    format: formatRate,
    label: "Verification Success Rate",
  },
  {
    value: showCount(SOURCE.apis_live),
    exact: SOURCE.apis_live,
    target: SOURCE.apis_live,
    format: formatCount,
    label: "Live Verification APIs",
  },
  {
    value: showCount(SOURCE.clients),
    exact: SOURCE.clients,
    target: SOURCE.clients,
    format: formatCount,
    label: "Businesses Onboarded",
  },
];
